import { css } from '@emotion/react';
import chroma from 'chroma-js';
import { getAutoColorCode } from '../../styles';
import { colors, semantic_colors, text_styles, shapes } from '../../foundation';
import {
  ButtonSize,
  ButtonKind,
  IconButtonShape,
  TextButtonKind,
  TextButtonSize,
  IconButtonKind,
  AccordionButtonSize,
  IconButtonSize,
} from './types';

export const button_base_css = css`
  display: flex;
  margin: 0;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-family: inherit;
  font-size: 13px;
  line-height: 22px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid transparent;
  transition: background-color 0.1s linear, border-color 0.1s linear, color 0.1s linear;
  ${shapes.border_radius.small};
  text-decoration: none;
  &:focus {
    outline: 3px solid ${semantic_colors.tab_focus};
    box-shadow: none;
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

function buildCSSWithMixedColor(
  color_property_key: 'color' | 'background-color' | 'border-color',
  color_property_value: string,
  mix_weight?: number,
  color_to_mix = 'black',
  reversed = false,
) {
  const color_value = !mix_weight
    ? color_property_value
    : reversed
    ? chroma.mix(color_to_mix, color_property_value, mix_weight).hex()
    : chroma.mix(color_property_value, color_to_mix, mix_weight).hex();
  return css({ [color_property_key]: color_value });
}

function getButtonStyle(
  { color, background_color, border_color }: { color?: string; background_color?: string; border_color?: string },
  mix_weight?: number,
  color_to_mix?: string,
  reversed?: boolean,
) {
  return css`
    ${color && buildCSSWithMixedColor('color', color, mix_weight, color_to_mix, reversed)}
    ${background_color &&
    buildCSSWithMixedColor('background-color', background_color, mix_weight, color_to_mix, reversed)}
    ${border_color && buildCSSWithMixedColor('border-color', border_color, mix_weight, color_to_mix, reversed)}
  `;
}

function getAutoButtonStyle({ color, background_color }: { color: string; background_color: string }) {
  const { hover, pressed } = getAutoColorCode(background_color);

  return css`
    color: ${color};
    background-color: ${background_color};
    &:hover:not([disabled]) {
      background-color: ${hover};
    }
    &:active:not([disabled]) {
      background-color: ${pressed};
    }
    &:disabled {
      opacity: 0.4;
    }
  `;
}

function getOutlinedButtonStyle({
  color,
  border_color,
  mix_weight = 0.1,
}: {
  color: string;
  border_color?: string;
  mix_weight?: number;
}) {
  const outlined_color = border_color ?? color;
  return css`
    ${getButtonStyle({
      color,
      border_color: outlined_color,
      background_color: semantic_colors.background.surface,
    })}
    &:hover:not([disabled]) {
      ${getButtonStyle({ background_color: outlined_color }, mix_weight / 2, 'white', true)}
    }
    &:active:not([disabled]) {
      ${getButtonStyle({ background_color: outlined_color }, mix_weight, 'white', true)}
    }
    &:disabled {
      opacity: 0.4;
    }
  `;
}

function getOutlinedIconButtonStyle({
  color,
  border_color,
  disabled_color,
  disabled_background_color,
  mix_weight = 0.1,
}: {
  color: string;
  border_color?: string;
  disabled_color: string;
  disabled_background_color?: string;
  mix_weight?: number;
}) {
  const outlined_color = border_color ?? color;
  return css`
    ${getButtonStyle({
      color,
      border_color: outlined_color,
      background_color: semantic_colors.background.surface,
    })}
    &:hover {
      ${getButtonStyle({ background_color: outlined_color }, mix_weight / 2, 'white', true)}
    }
    &:active {
      ${getButtonStyle({ background_color: outlined_color }, mix_weight, 'white', true)}
    }
    &:disabled {
      ${getButtonStyle({
        color: disabled_color,
        border_color: disabled_color,
        background_color: disabled_background_color ?? semantic_colors.background.surface,
      })}
    }
  `;
}

function getDefaultButtonStyle({ color, mix_weight = 0.4 }: { color: string; mix_weight?: number }) {
  const border_color = semantic_colors.border.secondary;
  return css`
    ${getButtonStyle({
      color,
      background_color: semantic_colors.background.surface,
      border_color,
    })}
    &:hover {
      ${buildCSSWithMixedColor('background-color', border_color, mix_weight / 2, 'white', true)}
      ${buildCSSWithMixedColor('color', border_color, mix_weight / 2, color, true)}
    }
    &:active {
      ${buildCSSWithMixedColor('background-color', border_color, mix_weight, 'white', true)}
      ${buildCSSWithMixedColor('color', border_color, mix_weight, color, true)}
    }
    &:disabled {
      ${getButtonStyle({
        color: semantic_colors.content.disabled,
        background_color: semantic_colors.background.disabled,
        border_color,
      })}
    }
  `;
}

export const getButtonKind = (kind?: ButtonKind) => {
  switch (kind) {
    case 'outlined_primary':
      return getOutlinedButtonStyle({
        color: semantic_colors.accent.primary,
      });
    case 'outlined_secondary':
      return getOutlinedButtonStyle({
        color: semantic_colors.content.primary,
        border_color: semantic_colors.border.secondary,
        mix_weight: 0.4,
      });
    case 'outlined_negative':
      return getOutlinedButtonStyle({
        color: semantic_colors.state.negative,
      });
    case 'outlined_black':
      return getOutlinedButtonStyle({
        color: semantic_colors.content.primary,
      });
    case 'primary':
      return getAutoButtonStyle({
        color: semantic_colors.content.primary_inverse,
        background_color: semantic_colors.accent.primary,
      });
    case 'black':
      return getAutoButtonStyle({
        color: semantic_colors.content.primary_inverse,
        background_color: semantic_colors.background.surface_inverse,
      });
    case 'negative':
      return getAutoButtonStyle({
        color: semantic_colors.content.primary_inverse,
        background_color: semantic_colors.state.negative,
      });
    case 'secondary':
    default:
      return getAutoButtonStyle({
        color: semantic_colors.content.primary,
        background_color: semantic_colors.fill.secondary,
      });
  }
};

export const getIconButtonKind = (kind?: IconButtonKind) => {
  switch (kind) {
    case 'outlined_primary':
      return getOutlinedIconButtonStyle({
        color: semantic_colors.accent.primary,
        disabled_color: semantic_colors.accent.disabled,
      });
    case 'outlined_negative':
      return getOutlinedIconButtonStyle({
        color: semantic_colors.state.negative,
        disabled_color: semantic_colors.state.negative_disabled,
      });
    case 'outlined_secondary':
    default:
      return getDefaultButtonStyle({ color: semantic_colors.content.primary });
  }
};

export const button_size_css: Record<ButtonSize, string> = {
  xsmall: `
    height: 28px;
    padding: 0 8px;
    column-gap: 2px;
    ${text_styles.Body_12_Regular};
  `,
  small: `
    height: 32px;
    padding: 0 12px;
    column-gap: 4px;
    ${text_styles.Body_13_Medium};
  `,
  medium: `
    height: 38px;
    padding: 0 12px;
    column-gap: 4px;
    ${text_styles.Body_13_Medium};
  `,
  large: `
    height: 46px;
    padding: 0 20px;
    column-gap: 4px;
    ${text_styles.Body_14_Medium};
  `,
  xlarge: `
    height: 56px;
    padding: 0 24px;
    column-gap: 8px;
    ${text_styles.Body_15_SemiBold};
  `,
  xxlarge: `
    height: 64px;
    padding: 0 28px;
    column-gap: 8px;
    ${text_styles.Heading_18_SemiBold};
  `,
};

export const icon_button_size_css: Record<IconButtonSize, string> = {
  xxsmall: `
    width: 20px;
    height: 20px;
  `,
  xsmall: `
    width: 28px;
    height: 28px;
  `,
  small: `
    width: 32px;
    height: 32px;
  `,
  medium: `
    width: 38px;
    height: 38px;
  `,
  large: `
    width: 46px;
    height: 46px;
  `,
  xlarge: `
    width: 56px;
    height: 56px;
  `,
  xxlarge: `
    width: 64px;
    height: 64px;
  `,
};

export const button_icon_size_css: Record<IconButtonSize, number> = {
  xxsmall: 10,
  xsmall: 12,
  small: 14,
  medium: 14,
  large: 16,
  xlarge: 20,
  xxlarge: 20,
};

export const button_size_icon_size_css: Record<ButtonSize, number> = {
  xsmall: 12,
  small: 14,
  medium: 14,
  large: 16,
  xlarge: 20,
  xxlarge: 20,
};

export const text_button_icon_size_css: Record<TextButtonSize, number> = {
  small: 12,
  medium: 14,
  large: 16,
};

export const getIconButtonStyle = (shape?: IconButtonShape) => {
  switch (shape) {
    case 'circle':
      const default_style = {
        color: semantic_colors.content.primary_inverse,
        background_color: chroma(colors.gray800).alpha(0.6).css(),
      };
      return css`
        width: 28px;
        height: 28px;
        padding: 4px;
        gap: 10px;
        border-radius: 50%;
        ${getButtonStyle(default_style)}
        &:hover {
          ${getButtonStyle(default_style, 0.1)}
        }
        &:active {
          ${getButtonStyle(default_style, 0.2)}
        }
        &:disabled {
          ${getButtonStyle({
            color: semantic_colors.content.primary_inverse,
            background_color: chroma(colors.gray800).alpha(0.2).css(),
          })}
        }
      `;
    case 'rectangle':
    default:
      return css`
        width: 46px;
        height: 46px;
        padding: 0;
      `;
  }
};

export const text_button_text_css: Record<TextButtonSize, string> = {
  small: text_styles.Body_12_Regular,
  medium: text_styles.Body_13_Regular,
  large: text_styles.Body_14_Regular,
};

export const text_button_size_css: Record<TextButtonSize, string> = {
  small: `
    padding: 0 6px;
    height: 24px;
  `,
  medium: `
    padding: 0 8px;
    height: 26px;
  `,
  large: `
    padding: 0 8px;
    height: 28px;
  `,
};

export const getTextButtonStyle = (kind: TextButtonKind, fixed_color: string) => {
  const { hover, pressed } = getAutoColorCode(fixed_color);
  const base_style = css`
    column-gap: 4px;
    background-color: transparent;
    color: ${fixed_color};
    text-decoration-color: ${fixed_color};
    text-underline-offset: 2px;
  `;

  switch (kind) {
    case 'link':
      return css`
        ${base_style};
        padding: 0;
        &:hover {
          color: ${hover};
          text-decoration-color: ${hover};
        }
        &:active {
          color: ${pressed};
          text-decoration-color: ${pressed};
        }
        &:disabled {
          color: ${fixed_color};
          opacity: 0.4;
        }
      `;
    case 'text':
    default:
      return css`
        ${base_style};
        ${shapes.border_radius.xsmall};
        &:hover {
          ${getButtonStyle({
            color: fixed_color,
            background_color: colors.gray10,
          })}
        }
        &:active {
          ${getButtonStyle({
            color: fixed_color,
            background_color: colors.gray20,
          })}
        }
        &:disabled {
          ${getButtonStyle({
            color: fixed_color,
            background_color: 'transparent',
          })}
          text-decoration-color: ${fixed_color};
          opacity: 0.4;
        }
      `;
  }
};

export const accordion_button_css = css`
  border: 1px solid ${semantic_colors.border.secondary};
  ${text_styles.Body_13_Medium};
  ${getAutoButtonStyle({
    color: semantic_colors.content.primary,
    background_color: semantic_colors.background.surface,
  })}
`;

export const accordion_button_size_css: Record<AccordionButtonSize, string> = {
  xsmall: `
    padding: 3px 8px 3px 12px;
    height: 28px;
    gap: 4px;
  `,
  medium: `
    padding: 8px 12px 8px 16px;
    height: 38px;
    gap: 8px;
  `,
};

export const floating_button_css = css`
  position: relative;
  ${text_styles.Body_14_SemiBold};
  border-radius: 100px;
  ${shapes.shadow.medium};
  border: none;
  transition: width 0.3s ease-in-out;
  :focus,
  :active {
    ${shapes.shadow.medium};
    transition: width 0.3s ease-in-out, background-color 0.1s linear, border-color 0.1s linear, color 0.1s linear;
  }
  :hover {
    transition: width 0.3s ease-in-out, background-color 0.1s linear, border-color 0.1s linear, color 0.1s linear;
  }
`;

export const getFloatingButtonCss = (opened: boolean, width?: number) => {
  if (opened) {
    return `
      width: 40px;
      padding: 10px;
    `;
  } else {
    return `
      width: ${width ? width + 'px' : 'auto'};
      padding: 10px 16px;
    `;
  }
};
